import {
    type IncidentAiAnalysisStatus,
    IncidentAiAnalysisStatus as IncidentAiAnalysisStatuses,
} from '../../prisma/enums';

/**
 * Mutable incident fields consumed by obligation retrieval or the AI prompt.
 *
 * Keep this list aligned with the analyser projection. The server-side value
 * comparison consumes it to decide whether a persisted edit requires fresh
 * analysis, while the shared contract test guards against projection drift.
 * `dtClosed` is excluded because it is derived from `status` by the incident
 * command service.
 */
export const INCIDENT_AI_ANALYSIS_TRIGGER_FIELDS = [
    'description',
    'products',
    'productImpacted',
    'rootCauseDescription',
    'rootCauseIds',
    'impactDescription',
    'consequenceManagement',
    'comments',
    'status',
    'dtIdentified',
    'dateOccurred',
    'dtRectified',
    'isSignificant',
    'typeId',
    'impactedPartyIds',
] as const;

export type IncidentAiAnalysisTriggerField = (typeof INCIDENT_AI_ANALYSIS_TRIGGER_FIELDS)[number];

/**
 * Quiet window before the server analyses an edit-driven save. Saves in one
 * edit session arrive seconds apart, so each save restarts the window and the
 * burst becomes one generation. The incident page keeps polling fast past this
 * deadline to discover the delayed run, all within its 120 s save-initiated
 * polling window (`aiAnalysisPolling.ts`).
 */
export const INCIDENT_AI_ANALYSIS_DEBOUNCE_MS = 30_000;

export const INCIDENT_AI_ANALYSIS_ACTIVE_STATUSES = [
    IncidentAiAnalysisStatuses.PENDING,
    IncidentAiAnalysisStatuses.PROCESSING,
] as const satisfies readonly IncidentAiAnalysisStatus[];

export const INCIDENT_AI_ANALYSIS_SETTLED_STATUSES = [
    IncidentAiAnalysisStatuses.COMPLETED,
    IncidentAiAnalysisStatuses.FAILED,
] as const satisfies readonly IncidentAiAnalysisStatus[];

const ACTIVE_INCIDENT_AI_ANALYSIS_STATUS_SET = new Set<IncidentAiAnalysisStatus>(INCIDENT_AI_ANALYSIS_ACTIVE_STATUSES);
const SETTLED_INCIDENT_AI_ANALYSIS_STATUS_SET = new Set<IncidentAiAnalysisStatus>(
    INCIDENT_AI_ANALYSIS_SETTLED_STATUSES,
);

export function isActiveIncidentAiAnalysisStatus(
    status: IncidentAiAnalysisStatus | null | undefined,
): status is (typeof INCIDENT_AI_ANALYSIS_ACTIVE_STATUSES)[number] {
    return status != null && ACTIVE_INCIDENT_AI_ANALYSIS_STATUS_SET.has(status);
}

export function isSettledIncidentAiAnalysisStatus(
    status: IncidentAiAnalysisStatus | null | undefined,
): status is (typeof INCIDENT_AI_ANALYSIS_SETTLED_STATUSES)[number] {
    return status != null && SETTLED_INCIDENT_AI_ANALYSIS_STATUS_SET.has(status);
}
